// src/components/layout/Header.tsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import type { TelegramUserInfoDTO } from "@/hooks/useTelegramAuth";
import { useNotificationsStore, type AdminNotificationItem } from "@/store/notificationsStore";
import { useReservationsUiStore, type ReservationTabKey } from "@/store/reservationsUiStore";
import { useSettingsUiStore } from "@/store/settingsUiStore";
import { APP_VERSION } from "@/lib/appVersion";

interface HeaderProps {
    user: TelegramUserInfoDTO | null;

    // переключить основной таб приложения на "reservations"
    goToReservations: () => void;

    /** Экран «Настройки» (в т.ч. заявки на доступ в браузерный кабинет). */
    goToSettings: () => void;

    // 👇 добавляем
    showLogout?: boolean;
    onLogout?: () => void;
}

function formatTime(ts: number) {
    const d = new Date(ts);
    return d.toLocaleTimeString("ru-RU", { hour: "2-digit", minute: "2-digit" });
}

function mapReservationTypeToTab(type: string | undefined): ReservationTabKey {
    switch ((type || "").toUpperCase()) {
        case "ADULT":
            return "ADULT";
        case "CHILD_LESS_THEN_EIGHT":
            return "CHILD_LESS_THEN_EIGHT";
        case "CHILD_ABOVE_EIGHT":
            return "CHILD_ABOVE_EIGHT";
        case "CHILD_TWELVE_TO_SIXTEEN":
            return "CHILD_TWELVE_TO_SIXTEEN";
        case "COURT":
            return "COURT";
        case "MEMBERSHIP":
            return "MEMBERSHIP";
        default:
            return "ALL";
    }
}

export const Header: React.FC<HeaderProps> = ({ user, goToReservations, goToSettings,
                                                  showLogout = false,
                                                  onLogout}) => {
    const displayName =
        user?.firstName || user?.lastName
            ? [user?.firstName, user?.lastName].filter(Boolean).join(" ")
            : user?.username
                ? `@${user.username}`
                : null;

    const items = useNotificationsStore((s) => s.items);
    const unreadCount = useNotificationsStore((s) => s.unreadCount);
    const markRead = useNotificationsStore((s) => s.markRead);
    const markAllRead = useNotificationsStore((s) => s.markAllRead);

    const setReservationsTab = useReservationsUiStore((s) => s.setTab);

    const [open, setOpen] = useState(false);
    const boxRef = useRef<HTMLDivElement | null>(null);

    const topItems = useMemo(() => items.slice(0, 10), [items]);

    useEffect(() => {
        if (!open) return;

        const onKeyDown = (e: KeyboardEvent) => {
            if (e.key === "Escape") setOpen(false);
        };

        // закрытие по клику вне области
        const onPointerDown = (e: PointerEvent) => {
            if (!boxRef.current) return;
            if (!boxRef.current.contains(e.target as Node)) setOpen(false);
        };

        window.addEventListener("keydown", onKeyDown);
        window.addEventListener("pointerdown", onPointerDown);
        return () => {
            window.removeEventListener("keydown", onKeyDown);
            window.removeEventListener("pointerdown", onPointerDown);
        };
    }, [open]);

    const onClickNotification = (n: AdminNotificationItem) => {
        markRead(n.id);

        if (n.eventType === "BROWSER_ACCESS_PENDING") {
            useSettingsUiStore.getState().requestNavigateToTab("BROWSER_ACCESS");
            goToSettings();
            setOpen(false);
            return;
        }

        const tab = mapReservationTypeToTab(n.reservationType);
        setReservationsTab(tab);
        goToReservations();

        setOpen(false);
    };

    // ✅ Отступ сверху внутри header на случай, если safe-area в App.tsx ещё не применился
    // (не помешает в браузере, и спасает "первый кадр" в Telegram)
    const headerStyle: React.CSSProperties = {
        paddingTop: "calc(var(--tg-safe-area-inset-top, env(safe-area-inset-top, 0px)) + 4px)",
    };

    return (
        <header
            style={headerStyle}
            className={[
                "relative",
                "flex items-start justify-between gap-3",
                // На узких экранах переносим правый блок вниз (чтобы ничего не наезжало)
                "flex-wrap sm:flex-nowrap",
            ].join(" ")}
        >
            {/* LEFT */}
            <div className="min-w-0 flex-1">
                <h1 className="text-lg font-semibold truncate">Панель клуба: заявки и расписание</h1>
                <p className="text-xs text-slate-400 truncate">Заявки, корты, рассылки · v{APP_VERSION}</p>
            </div>

            {/* RIGHT */}
            <div
                ref={boxRef}
                className={[
                    "flex items-center gap-2",
                    "shrink-0",
                    // если header переносится, на мобилке правый блок уходит на следующую строку и прижимается вправо
                    "ml-auto",
                ].join(" ")}
            >
                {/* 🔔 Bell */}
                <div className="relative">
                    <button
                        type="button"
                        onClick={() => setOpen((v) => !v)}
                        className={[
                            "relative inline-flex h-10 w-10 items-center justify-center rounded-2xl border",
                            "border-slate-800 bg-slate-900/70 text-slate-200 hover:border-sky-500/60",
                            open ? "border-sky-500/70" : "",
                        ].join(" ")}
                        aria-label="Уведомления"
                        title="Уведомления"
                    >
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" className="opacity-90">
                            <path
                                d="M12 22a2.5 2.5 0 0 0 2.45-2h-4.9A2.5 2.5 0 0 0 12 22Zm7-6V11a7 7 0 1 0-14 0v5l-2 2v1h18v-1l-2-2Z"
                                stroke="currentColor"
                                strokeWidth="1.7"
                                strokeLinejoin="round"
                            />
                        </svg>

                        {unreadCount > 0 && (
                            <span className="absolute -top-1 -right-1 inline-flex min-w-[18px] items-center justify-center rounded-full bg-sky-500 px-1.5 py-0.5 text-[10px] font-semibold text-white">
                {unreadCount > 99 ? "99+" : unreadCount}
              </span>
                        )}
                    </button>

                    {/* dropdown */}
                    {open && (
                        <div
                            className={[
                                "absolute right-0 z-50 mt-2",
                                "w-[min(92vw,420px)] overflow-hidden rounded-2xl",
                                "border border-slate-800 bg-slate-950 shadow-xl",
                            ].join(" ")}
                        >
                            <div className="flex items-center justify-between gap-2 border-b border-slate-800 px-3 py-2">
                                <div className="min-w-0">
                                    <div className="text-xs font-semibold text-slate-100">Уведомления</div>
                                    <div className="text-[11px] text-slate-400 truncate">
                                        {unreadCount > 0 ? `Непрочитанных: ${unreadCount}` : "Нет новых уведомлений"}
                                    </div>
                                </div>

                                <div className="flex items-center gap-2 shrink-0">
                                    {items.length > 0 && unreadCount > 0 && (
                                        <button
                                            type="button"
                                            onClick={() => markAllRead()}
                                            className="rounded-xl border border-slate-700 px-2 py-1 text-[11px] text-slate-200 hover:bg-slate-900"
                                        >
                                            Прочитать всё
                                        </button>
                                    )}
                                    <button
                                        type="button"
                                        onClick={() => setOpen(false)}
                                        className="rounded-xl border border-slate-700 px-2 py-1 text-[11px] text-slate-200 hover:bg-slate-900"
                                    >
                                        Закрыть
                                    </button>
                                </div>
                            </div>

                            <div className="max-h-[360px] overflow-auto">
                                {topItems.length === 0 ? (
                                    <div className="px-3 py-3 text-[11px] text-slate-400">Пока ничего нет.</div>
                                ) : (
                                    <div className="divide-y divide-slate-800">
                                        {topItems.map((n) => (
                                            <button
                                                key={n.id}
                                                type="button"
                                                onClick={() => onClickNotification(n)}
                                                className={[
                                                    "w-full px-3 py-3 text-left transition-colors",
                                                    n.read ? "bg-slate-950" : "bg-slate-900/40",
                                                    "hover:bg-slate-900/70",
                                                ].join(" ")}
                                            >
                                                <div className="flex items-start justify-between gap-2">
                                                    <div className="min-w-0 text-[11px] font-semibold text-slate-100 truncate">{n.title}</div>
                                                    <div className="shrink-0 text-[10px] text-slate-500">{formatTime(n.createdAt)}</div>
                                                </div>

                                                <div className="mt-1 text-[11px] text-slate-400">
                                                    {n.username && <span className="text-slate-200">{n.username}</span>}
                                                    {n.phoneNumber && (
                                                        <>
                                                            {" · "}
                                                            <span className="text-slate-300">{n.phoneNumber}</span>
                                                        </>
                                                    )}
                                                </div>

                                                {!n.read && (
                                                    <div className="mt-2">
                            <span className="inline-flex rounded-full bg-sky-500/15 px-2 py-0.5 text-[10px] text-sky-200">
                              Новое
                            </span>
                                                    </div>
                                                )}
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>

                {/* Admin badge */}
                {user && (
                    <div className="flex flex-col items-end gap-1 max-w-[60vw] sm:max-w-[42vw]">
            <span className="inline-flex items-center rounded-xl bg-sky-500/10 px-3 py-1 text-[11px] font-medium text-sky-200 border border-sky-500/30 max-w-full">
              <span className="shrink-0">Админ</span>
                {displayName && <span className="ml-1 text-sky-50 truncate max-w-[44vw] sm:max-w-[28vw]">{displayName}</span>}
            </span>
                        {user.username && <span className="text-[10px] text-slate-500 truncate max-w-full">@{user.username}</span>}
                    </div>
                )}
                {/* Logout button (browser only) */}
                {showLogout && (
                    <button
                        type="button"
                        onClick={onLogout}
                        className="inline-flex h-10 items-center justify-center rounded-2xl border border-red-500/30 bg-red-500/10 px-3 text-[11px] font-medium text-red-200 hover:bg-red-500/20"
                    >
                        Выйти
                    </button>
                )}
            </div>
        </header>
    );
};
